import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Table,
  Text,
  Loader,
  Center,
  Badge,
  Button,
  Modal,
  Group,
  Stack,
  Title,
  TextInput,
  Select,
  Paper,
  ThemeIcon,
  ScrollArea,
  CloseButton,
  Pagination,
  Timeline,
  CopyButton,
  Tooltip,
  ActionIcon,
  Tabs,
} from "@mantine/core";
// import {
//   IconSearch,
//   IconFilterOff,
//   IconVideo,
//   IconEye,
//   IconCopy,
//   IconCheck,
//   IconCircleCheck,
//   IconCircleX,
//   IconClock,
//   IconChartPie,
// } from "@tabler/icons-react";

import {
  IconSearch,
  IconFilterOff,
  IconVideo,
  IconEye,
  IconCopy,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconChartPie,
  IconRefresh,
} from "@tabler/icons-react";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { ApiService } from "../api/api-service";

// Shared axios instance (already configured with baseURL + auth token
// interceptor in api-service.ts) — matches the ApiService.getInstance()
// pattern used elsewhere in this codebase.
const apiClient = ApiService.getInstance();

const BASE_PATH = "/recording-tracker";
const PAGE_LIMIT = 20;

// =====================================================================
// Types
// =====================================================================

interface TimelineEntry {
  Stage: string;
  Status: string;
  Timestamp: string;
  Error?: string;
}

interface RecordingRecord {
  recording_id: string;
  account_id: string;
  Batch_Name: string;
  Course_ID: string;
  Course_Title?: string;
  CreatedAt: string;
  CreatedDate: string;
  CurrentStatus: string;
  Download_Date_Time_IST?: string;
  Duration?: string;
  duration_seconds?: number;
  embedcode?: string;
  Embedcode_Status?: string;
  ErrorCode?: string;
  ErrorMessage?: string;
  FailedStage?: string;
  File_Name?: string;
  File_Upload_Workdrive?: string;
  FolderName?: string;
  GmeetRecording_Status?: string;
  Google_File_ID?: string;
  LastUpdatedAt?: string;
  Lesson_Creation_Status?: string;
  Lesson_Link?: string;
  App_Videos_Status?: string;
  App_Videos_Sno?: number;
  App_Videos_Error?: string;
  App_Videos_Updated_At?: string;
  Local_File_Path?: string;
  meeting_id?: string;
  RequiresAttention?: boolean;
  server?: string;
  Timeline?: TimelineEntry[];
  Topic_ID?: string;
  Topic_Name?: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type DateBucket = "Today" | "Yesterday" | "Last7Days" | "MoreThan7Days";

interface BucketStats {
  total: number;
  statusCounts: Record<string, number>;
}

type DateRangeStatsResponse = Record<DateBucket, BucketStats>;

// =====================================================================
// Small shared helpers
// =====================================================================

function statusColor(status?: string) {
  if (!status) return "gray";
  const s = status.toUpperCase();
  if (s.includes("SUCCESS") || s.includes("ADDED") || s.includes("GENERATED") || s.includes("DONE")) return "green";
  if (s.includes("FAIL") || s.includes("ERROR")) return "red";
  return "yellow";
}

function statusLabel(status?: string) {
  return status && status.trim() ? status : "Not Started";
}

// Maps the same status buckets used by statusColor() to concrete hex
// values, since recharts needs real CSS colors rather than Mantine
// theme color names.
function statusHexColor(status: string) {
  const color = statusColor(status);
  switch (color) {
    case "green":
      return "#40c057";
    case "red":
      return "#fa5252";
    case "yellow":
      return "#fab005";
    default:
      return "#adb5bd";
  }
}

function formatDuration(duration?: string) {
  if (!duration) return "-";
  // Duration is stored like "00-57-15" (HH-MM-SS)
  return duration.replace(/-/g, ":");
}

// Prefers duration_seconds (converted to whole minutes) when present;
// falls back to the older HH-MM-SS string field for records that
// predate duration_seconds.
function formatDurationMinutes(durationSeconds?: number, fallback?: string) {
  if (durationSeconds !== undefined && durationSeconds !== null) {
    const minutes = Math.round(durationSeconds / 60);
    return `${minutes} min`;
  }
  return formatDuration(fallback);
}

function EmptyState({ message }: { message: string }) {
  return (
    <Center py={60}>
      <Stack align="center" gap={6}>
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconVideo size={24} />
        </ThemeIcon>
        <Text c="dimmed" size="sm">
          {message}
        </Text>
      </Stack>
    </Center>
  );
}

function LoadingState() {
  return (
    <Center py={60}>
      <Stack align="center" gap={8}>
        <Loader size="md" color="indigo" />
        <Text size="xs" c="dimmed">
          Loading...
        </Text>
      </Stack>
    </Center>
  );
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================

export default function RecordingTracker() {
  const [records, setRecords] = useState<RecordingRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: PAGE_LIMIT,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [failedStageOptions, setFailedStageOptions] = useState<string[]>([]);
  const [detailRecord, setDetailRecord] = useState<RecordingRecord | null>(null);

  // Raw text inputs (typed immediately) vs debounced values actually
  // sent to the backend, so we don't fire a request on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [batchInput, setBatchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedBatch, setDebouncedBatch] = useState("");

  const [status, setStatus] = useState<string | null>(null);
  const [failedStage, setFailedStage] = useState<string | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);

  // Pie chart modal state
  const [pieModalOpen, setPieModalOpen] = useState(false);
  const [pieLoading, setPieLoading] = useState(false);
  const [dateRangeStats, setDateRangeStats] = useState<DateRangeStatsResponse | null>(null);
  const [activeStatsTab, setActiveStatsTab] = useState<DateBucket>("Today");

  // Debounce search/batch text inputs by 400ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedBatch(batchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [batchInput]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedBatch, status, failedStage, startDate, endDate]);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/recordings`, {
        params: {
          page,
          limit: PAGE_LIMIT,
          search: debouncedSearch || undefined,
          batchName: debouncedBatch || undefined,
          status: status || undefined,
          failedStage: failedStage || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      setRecords(res.data?.data || []);
      setPagination(
        res.data?.pagination || { page: 1, limit: PAGE_LIMIT, total: 0, totalPages: 1 },
      );
    } catch (err) {
      console.error("Failed to fetch recordings:", err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, debouncedBatch, status, failedStage, startDate, endDate]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`${BASE_PATH}/recordings/statuses`);
        setStatusOptions(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch statuses:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get(`${BASE_PATH}/recordings/failed-stages`);
        setFailedStageOptions(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch failed stages:", err);
      }
    })();
  }, []);

  const fetchDateRangeStats = useCallback(async () => {
    try {
      setPieLoading(true);
      const res = await apiClient.get(`${BASE_PATH}/recordings/date-range-stats`);
      setDateRangeStats(res.data?.data || null);
    } catch (err) {
      console.error("Failed to fetch date range stats:", err);
    } finally {
      setPieLoading(false);
    }
  }, []);

  const hasFilters = useMemo(
    () =>
      !!(
        searchInput.trim() ||
        batchInput.trim() ||
        status ||
        failedStage ||
        startDate ||
        endDate
      ),
    [searchInput, batchInput, status, failedStage, startDate, endDate],
  );

  const clearFilters = () => {
    setSearchInput("");
    setBatchInput("");
    setStatus(null);
    setFailedStage(null);
    setStartDate("");
    setEndDate("");
  };

  // Failed Stage / Error Message columns are only shown once the user
  // has applied the Failed Stage filter.
  const showFailureColumns = !!failedStage;

  return (
    <Box>
      {/* <Group justify="space-between" mb="lg" wrap="wrap">
        <Title order={3}>Recording Tracker</Title>
        <Button
          variant="light"
          color="indigo"
          leftSection={<IconChartPie size={16} />}
          onClick={() => {
            setPieModalOpen(true);
            fetchDateRangeStats();
          }}
        >
          Pie Chart Analysis
        </Button>
      </Group> */}
      <Group justify="space-between" mb="lg" wrap="wrap">
  <Title order={3}>Recording Tracker</Title>
  <Group gap="sm">
    <Button
      variant="light"
      color="gray"
      leftSection={<IconRefresh size={16} />}
      onClick={() => fetchRecords()}
      loading={loading}
    >
      Refresh
    </Button>
    <Button
      variant="light"
      color="indigo"
      leftSection={<IconChartPie size={16} />}
      onClick={() => {
        setPieModalOpen(true);
        fetchDateRangeStats();
      }}
    >
      Pie Chart Analysis
    </Button>
  </Group>
</Group>

      {/* Filter bar */}
      <Paper withBorder radius="md" p="sm" mb="md" bg="gray.0">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap" style={{ flex: 1 }}>
            <TextInput
              placeholder="Search recording, meeting, file, account..."
              leftSection={<IconSearch size={14} />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              rightSection={
                searchInput ? (
                  <CloseButton size="xs" onClick={() => setSearchInput("")} aria-label="Clear search" />
                ) : null
              }
              radius="md"
              size="sm"
              style={{ minWidth: 260 }}
            />
            <TextInput
              placeholder="Filter by batch..."
              value={batchInput}
              onChange={(e) => setBatchInput(e.currentTarget.value)}
              rightSection={
                batchInput ? (
                  <CloseButton size="xs" onClick={() => setBatchInput("")} aria-label="Clear batch filter" />
                ) : null
              }
              radius="md"
              size="sm"
              style={{ minWidth: 200 }}
            />
            <Select
              placeholder="Status"
              data={statusOptions}
              value={status}
              onChange={setStatus}
              clearable
              radius="md"
              size="sm"
              style={{ minWidth: 200 }}
            />
            <Select
              placeholder="Failed Stage"
              data={failedStageOptions}
              value={failedStage}
              onChange={setFailedStage}
              clearable
              radius="md"
              size="sm"
              style={{ minWidth: 200 }}
            />
            <TextInput
              type="date"
              label={undefined}
              placeholder="Start date"
              value={startDate}
              onChange={(e) => setStartDate(e.currentTarget.value)}
              radius="md"
              size="sm"
            />
            <TextInput
              type="date"
              placeholder="End date"
              value={endDate}
              onChange={(e) => setEndDate(e.currentTarget.value)}
              radius="md"
              size="sm"
            />
            {hasFilters && (
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                radius="md"
                leftSection={<IconFilterOff size={14} />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </Group>
          <Text size="xs" c="dimmed" fw={500}>
            {pagination.total} total
          </Text>
        </Group>
      </Paper>

      {loading ? (
        <LoadingState />
      ) : records.length === 0 ? (
        <EmptyState message="No recordings match your filters." />
      ) : (
        <>
          <Paper withBorder radius="md">
            <ScrollArea>
              <Table verticalSpacing={6} horizontalSpacing="sm" highlightOnHover>
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th>Recording ID</Table.Th>
                    <Table.Th>Lesson Creation Status</Table.Th>
                    <Table.Th>App-Videos Status</Table.Th>
                    <Table.Th>Batch</Table.Th>
                    <Table.Th>Account</Table.Th>
                    <Table.Th>Course ID</Table.Th>
                    <Table.Th>Course Title</Table.Th>
                    <Table.Th ta="center">Duration</Table.Th>
                    <Table.Th ta="center">Created</Table.Th>
                    {showFailureColumns && (
                      <>
                        <Table.Th ta="center">Failed Stage</Table.Th>
                        <Table.Th ta="center">Error Message</Table.Th>
                      </>
                    )}
                    <Table.Th ta="center" w={80}>
                      Details
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {records.map((r) => (
                    <Table.Tr key={r.recording_id}>
                      <Table.Td>
                        <Badge variant="outline" color="gray" radius="sm">
                          {r.recording_id}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" color={statusColor(r.Lesson_Creation_Status)} radius="sm">
                          {statusLabel(r.Lesson_Creation_Status)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" color={statusColor(r.App_Videos_Status)} radius="sm" title={r.App_Videos_Error}>
                          {r.App_Videos_Status || "Not Added"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{r.Batch_Name || "-"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{r.account_id || "-"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{r.Course_ID || "-"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{r.Course_Title || "-"}</Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Text size="sm">{formatDurationMinutes(r.duration_seconds, r.Duration)}</Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Text size="sm" c="dimmed">
                          {r.CreatedDate}
                        </Text>
                      </Table.Td>
                      {showFailureColumns && (
                        <>
                          <Table.Td ta="center">
                            <Text size="sm" c={r.FailedStage ? "red" : "dimmed"}>
                              {r.FailedStage || "-"}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="center">
                            <Text size="sm" c="dimmed" style={{ maxWidth: 260 }} lineClamp={2}>
                              {r.ErrorMessage}
                            </Text>
                          </Table.Td>
                        </>
                      )}
                      <Table.Td ta="center">
                        <ActionIcon variant="light" color="indigo" onClick={() => setDetailRecord(r)}>
                          <IconEye size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Paper>

          {pagination.totalPages > 1 && (
            <Group justify="center" mt="md">
              <Pagination value={page} onChange={setPage} total={pagination.totalPages} radius="md" />
            </Group>
          )}
        </>
      )}

      {/* Detail modal */}
      <Modal
        opened={!!detailRecord}
        onClose={() => setDetailRecord(null)}
        title={<Text fw={600}>{detailRecord?.recording_id}</Text>}
        size="lg"
        centered
        radius="md"
      >
        {detailRecord && (
          <Stack gap="md">
            <Group grow>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Batch
                </Text>
                <Text size="sm">{detailRecord.Batch_Name || "-"}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Topic
                </Text>
                <Text size="sm">{detailRecord.Topic_Name || "-"}</Text>
              </Box>
            </Group>

            <Group grow>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Meeting ID
                </Text>
                <Text size="sm">{detailRecord.meeting_id}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Server
                </Text>
                <Text size="sm">{detailRecord.server}</Text>
              </Box>
            </Group>

            {detailRecord.File_Name && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  File Name
                </Text>
                <Text size="sm" style={{ wordBreak: "break-all" }}>
                  {detailRecord.File_Name}
                </Text>
              </Box>
            )}

            {(detailRecord.FailedStage || detailRecord.ErrorMessage) && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Failure Details
                </Text>
                {detailRecord.FailedStage && (
                  <Text size="sm">
                    Stage: <Badge color="red" variant="light" radius="sm">{detailRecord.FailedStage}</Badge>
                  </Text>
                )}
                {detailRecord.ErrorMessage && (
                  <Paper withBorder p="xs" radius="sm" bg="red.0" mt={4}>
                    <Text size="xs" c="red.8">
                      {detailRecord.ErrorMessage}
                    </Text>
                  </Paper>
                )}
              </Box>
            )}

            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                App-Videos Status
              </Text>
              <Badge variant="light" color={statusColor(detailRecord.App_Videos_Status)} radius="sm">
                {detailRecord.App_Videos_Status || "Not Added"}
              </Badge>
              {detailRecord.App_Videos_Sno !== undefined && (
                <Text size="sm" mt={4}>Sno: {detailRecord.App_Videos_Sno}</Text>
              )}
              {detailRecord.App_Videos_Error && (
                <Paper withBorder p="xs" radius="sm" bg="red.0" mt={4}>
                  <Text size="xs" c="red.8">{detailRecord.App_Videos_Error}</Text>
                </Paper>
              )}
            </Box>

            {detailRecord.Lesson_Link && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Lesson Link
                </Text>
                <Text size="sm" component="a" href={detailRecord.Lesson_Link} target="_blank" c="indigo">
                  {detailRecord.Lesson_Link}
                </Text>
              </Box>
            )}

            {detailRecord.embedcode && (
              <Box>
                <Group justify="space-between" mb={4}>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Embed Code
                  </Text>
                  <CopyButton value={detailRecord.embedcode}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied" : "Copy"}>
                        <ActionIcon size="sm" variant="light" color={copied ? "green" : "gray"} onClick={copy}>
                          {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
                <Paper withBorder p="xs" radius="sm" bg="gray.0">
                  <Text size="xs" style={{ wordBreak: "break-all" }}>
                    {detailRecord.embedcode}
                  </Text>
                </Paper>
              </Box>
            )}

            {detailRecord.Timeline && detailRecord.Timeline.length > 0 && (
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                  Timeline
                </Text>
                <Timeline active={detailRecord.Timeline.length} bulletSize={20} lineWidth={2}>
                  {detailRecord.Timeline.map((t, idx) => (
                    <Timeline.Item
                      key={`${t.Stage}-${idx}`}
                      bullet={
                        t.Status === "SUCCESS" ? (
                          <IconCircleCheck size={12} />
                        ) : t.Status === "FAILED" ? (
                          <IconCircleX size={12} />
                        ) : (
                          <IconClock size={12} />
                        )
                      }
                      title={t.Stage}
                    >
                      <Text size="xs" c="dimmed">
                        {t.Timestamp}
                      </Text>
                      {t.Error && (
                        <Text size="xs" c="red">
                          {t.Error}
                        </Text>
                      )}
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Box>
            )}
          </Stack>
        )}
      </Modal>

      {/* Pie chart / date-range analysis modal */}
      <Modal
        opened={pieModalOpen}
        onClose={() => setPieModalOpen(false)}
        title={<Text fw={600}>Recordings by Created Date</Text>}
        size="lg"
        centered
        radius="md"
      >
        {pieLoading ? (
          <LoadingState />
        ) : dateRangeStats ? (
          <Tabs value={activeStatsTab} onChange={(v) => v && setActiveStatsTab(v as DateBucket)}>
            <Tabs.List>
              <Tabs.Tab value="Today">Today ({dateRangeStats.Today.total})</Tabs.Tab>
              <Tabs.Tab value="Yesterday">Yesterday ({dateRangeStats.Yesterday.total})</Tabs.Tab>
              <Tabs.Tab value="Last7Days">Last 7 Days ({dateRangeStats.Last7Days.total})</Tabs.Tab>
              <Tabs.Tab value="MoreThan7Days">More Than 7 Days ({dateRangeStats.MoreThan7Days.total})</Tabs.Tab>
            </Tabs.List>

            {(["Today", "Yesterday", "Last7Days", "MoreThan7Days"] as const).map((bucket) => {
              const bucketData = dateRangeStats[bucket];
              const chartData = Object.entries(bucketData.statusCounts).map(([statusName, count]) => ({
                name: statusName,
                value: count,
              }));

              return (
                <Tabs.Panel key={bucket} value={bucket} pt="md">
                  {chartData.length === 0 ? (
                    <EmptyState message="No records in this range." />
                  ) : (
                    <Box style={{ width: "100%", height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={(entry) => `${entry.name}: ${entry.value}`}
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={statusHexColor(entry.name)} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </Tabs.Panel>
              );
            })}
          </Tabs>
        ) : (
          <EmptyState message="No stats available." />
        )}
      </Modal>
    </Box>
  );
}